import React, { useState, useEffect, useMemo, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { Menu, Modal, Tooltip } from "antd"
import type { MenuProps } from "antd"
import { notify } from "shared/ui/Notifications/notify"
import { Editor as TiptapEditor } from "@tiptap/react"

import { useEngine } from "hooks/api/useEngine"
import { canApplyFormatting } from "utils/editorUtils"

import items from "./MenuItems"
import actions from "./MenuActions"

import { ToolbarIconButton } from "./ToolbarButtons.component"
import ProgressModal from "shared/ui/ProgressModal/ProgressModal.component"
import ExportToDecipherModal from "./ExportToDecipher/ExportToDecipherModal.component"
import ExportToConfirmitModal from "./ExportToConfirmit/ExportToConfirmitModal.component"
import AIConversionModal from "./AIConversion/AIConversionModal.component"
import ExportMapSourceFillModal from "features/QueryConsole/components/ExportMap/ExportMapSourceFillModal.component"
import SurveyTestDrawer from "features/SurveyTest/components/SurveyTestDrawer.component"
import {
  faArrowRightToBracket,
  faListOl,
  faUndo,
  faRedo,
  faSitemap,
  faPlus,
  faHighlighter,
  faComments,
  faClockRotateLeft,
  faBold,
  faItalic,
  faBook,
  faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons"
import {
  useAssistantToggle,
  ASSISTANT_OFF_TEXT,
  useOptionalAssistantScreen,
} from "features/Assistant"

import { useProjectStore, Project } from "stores/projectStore"
import TemplatesDialog, {
  TemplatesInitialView,
  TemplatesSaveState,
} from "./Templates/TemplatesDialog.component"
import { StagedBlock } from "types/template.types"
import ExportModal from "shared/ui/ExportModal/ExportModal.component"
import { Variant } from "types/questionnaire.types"
import {
  EXPORT_VARIABLE_TYPES,
  VARIABLE_STYLE,
  ConversionType,
  ConversionOptions,
} from "types/engineExport.types"
import { SelectOption, MenuClickEvent, UI_INPUT_TYPES } from "types/ui.types"
import { DOCS_LINKS } from "constants/docsLinks"
import { ROUTES } from "constants/routes"
import { PROJECT_TAB_KEYS } from "constants/projectTabs"

/** Opens and closes the assistant panel; greyed out, saying whom to ask, when the user's organisation does not have it. */
const AssistantToolbarToggle: React.FC<{ projectId: string }> = ({ projectId }) => {
  const { visible, available, open, toggle } = useAssistantToggle(projectId)
  if (!visible) return null
  return (
    <>
      <div className="toolbar-divider" />
      <ToolbarIconButton
        title={available ? "Assistant" : ASSISTANT_OFF_TEXT}
        icon={faWandMagicSparkles}
        active={open}
        disabled={!available}
        onClick={toggle}
      />
    </>
  )
}

// Define the props interface for QuestionnaireMenuBar
interface QuestionnaireMenuBarProps {
  /** Indicates if the template should be used in the master configuration */
  inMaster: boolean
  /** Indicates if the questionnaire is empty */
  qnrEmpty: boolean
  /** The editor instance used to manipulate the questionnaire */
  editor: TiptapEditor | null
  /** Indicates if the highlighting is enabled */
  highlightingEnabled: boolean
  /** Function to toggle the highlighting feature */
  toggleHighlighting: () => void
  /** The variants data that will be passed to the export form */
  variants: Variant[]
  /** The questionnaire's own languages (its #LANGUAGE line above any variant), for the test survey on Master */
  masterLanguages: string[]
  /** Function to check the questionnaire */
  checkQuestionnaire: (id: string) => Promise<void>
  /** Function to retrieve the notes for the questionnaire */
  getQuestionnaireNotes: () => string[]
  /** Function to toggle the line numbers in the editor */
  toggleLineNumbers: () => void
  /** Indicates if the line numbers are enabled */
  lineNumbersEnabled: boolean
  /** Function to toggle the element highlighting in the editor */
  toggleElementHighlighting: () => void
  /** Indicates if the element highlighting is enabled */
  elementHighlightingEnabled: boolean
  /** Function to toggle the structure panel */
  toggleStructurePanel?: () => void
  /** Indicates if the structure panel is visible */
  structurePanelVisible?: boolean
  /** Function to toggle the add element modal */
  toggleAddElementModal?: () => void
  /** Indicates if the add element modal is visible */
  addElementModalVisible?: boolean
  /** Function to refresh element highlighting */
  refreshHighlight?: () => void
  /** Ref to structure panel button for tour */
  structurePanelButtonRef?: React.RefObject<HTMLButtonElement | null>
  /** Ref to add element button for tour */
  addElementButtonRef?: React.RefObject<HTMLButtonElement | null>
  /** Ref to element highlight button for tour */
  elementHighlightButtonRef?: React.RefObject<HTMLButtonElement | null>
  /** Whether the editor is connecting to collaboration server */
  isConnecting?: boolean
  /** Function to toggle the comments panel */
  toggleCommentsPanel?: () => void
  /** Indicates if the comments panel is visible */
  commentsPanelVisible?: boolean
  /** Number of comments in the current document */
  commentsCount?: number
  /** Indicates new comments arrived via websocket */
  hasNewComments?: boolean
  /** Function to toggle the version history panel */
  toggleHistoryPanel?: () => void
  /** Indicates if the version history panel is visible */
  historyPanelVisible?: boolean
  /** Variant currently being edited ("" / undefined = MASTER) */
  currentVariant?: string
  /** Saves the questionnaire (the editor's manual save); `false` when the save was skipped or failed */
  onSave?: () => Promise<boolean | void>
  /** The questionnaire-wide controls for the top row, beside File and Insert (VariantsMenuBar) */
  toolbarExtras?: React.ReactNode
  /** Set to a function that runs a File/Insert menu action by its key, for the Editor's start screen */
  menuActionRef?: React.MutableRefObject<((key: string) => Promise<void>) | null>
  /**
   * Set to a function that opens the Templates dialog straight into a
   * block's template wizard, filled in with it (an assistant proposal);
   * `onStaged` runs when the user adds that block to the staging tray
   */
  templateProposalRef?: React.MutableRefObject<
    ((block: StagedBlock, onStaged?: () => void) => void) | null
  >
}

// Define interfaces for export functions
interface DatamapExportValues {
  template: boolean
  groupSections: boolean
  variableNames: EXPORT_VARIABLE_TYPES
  variableStyle: VARIABLE_STYLE
  includeQuestionText: boolean
}

interface QuestionnaireExportValues {
  languages: string[]
  variant: string
  type: ConversionType
  variableNames: EXPORT_VARIABLE_TYPES
  style: string
}

/** The Insert menu's Template row on a variant: greyed out, with the reason as a tooltip. */
const VARIANT_TEMPLATE_TIP = "Templates are added in Master"

/**
 * The menu items for where the editor is. On a variant, Insert > Template is
 * disabled (templates are added in Master).
 *
 * @param inMaster - Whether the editor is on Master
 * @returns The menu items
 */
const menuItemsFor = (inMaster: boolean): MenuProps["items"] => {
  if (inMaster) return items
  const disableTemplate = (
    list: NonNullable<MenuProps["items"]>
  ): NonNullable<MenuProps["items"]> =>
    list.map((item) => {
      if (!item) return item
      if (item.key === "template" && "label" in item) {
        return {
          ...item,
          disabled: true,
          label: (
            <Tooltip title={VARIANT_TEMPLATE_TIP} placement="right">
              <span>{item.label as React.ReactNode}</span>
            </Tooltip>
          ),
        } as typeof item
      }
      if ("children" in item && Array.isArray(item.children)) {
        return {
          ...item,
          children: disableTemplate(item.children),
        } as typeof item
      }
      return item
    })
  return disableTemplate(items ?? [])
}

/**
 * Functional component representing the menu bar for the questionnaire editor.
 *
 * @component
 * @param props - The component props
 * @returns The rendered menu bar component
 */
const QuestionnaireMenuBar: React.FC<QuestionnaireMenuBarProps> = (props) => {
  const project: Project = useProjectStore((state) => state.project)
  const assistantScreen = useOptionalAssistantScreen()
  const navigate = useNavigate()
  const {
    inMaster,
    qnrEmpty,
    editor,
    highlightingEnabled,
    toggleHighlighting,
    variants,
    masterLanguages,
    checkQuestionnaire,
    getQuestionnaireNotes,
    toggleLineNumbers,
    lineNumbersEnabled,
    toggleElementHighlighting,
    elementHighlightingEnabled,
    toggleStructurePanel,
    structurePanelVisible,
    toggleAddElementModal,
    addElementModalVisible,
    refreshHighlight,
    structurePanelButtonRef,
    addElementButtonRef,
    elementHighlightButtonRef,
    isConnecting = false,
    toggleCommentsPanel,
    commentsPanelVisible,
    hasNewComments = false,
    toggleHistoryPanel,
    historyPanelVisible,
    currentVariant,
    onSave,
    toolbarExtras,
    menuActionRef,
    templateProposalRef,
  } = props

  // Typed state variables
  const [isExportDecipherModalVisible, setExportDecipherModalVisible] =
    useState<boolean>(false)
  const [isExportConfirmitModalVisible, setExportConfirmitModalVisible] =
    useState<boolean>(false)
  const [isTemplatesOpen, setIsTemplatesOpen] = useState<boolean>(false)
  const [templatesSave, setTemplatesSave] = useState<TemplatesSaveState>("saved")
  const [templatesInitialView, setTemplatesInitialView] = useState<
    TemplatesInitialView | undefined
  >(undefined)
  // Runs when the initial view's block is added to the staging tray
  const templateProposalStaged = useRef<(() => void) | undefined>(undefined)

  /**
   * The save the templates need (the wizard reads the SAVED questionnaire).
   * Runs while the dialog is already open; `false` = skipped (e.g.
   * disconnected) or failed.
   */
  const saveForTemplates = async (): Promise<void> => {
    setTemplatesSave("saving")
    let saved: boolean | void = true
    try {
      if (onSave) saved = await onSave()
    } catch (error) {
      console.error("Error saving before templates:", error)
      saved = false
    }
    setTemplatesSave(saved === false ? "failed" : "saved")
  }
  const [isExportDatamapModalVisible, setIsExportDatamapModalVisible] =
    useState<boolean>(false)
  const [
    isExportQuestionnaireModalVisible,
    setIsExportQuestionnaireModalVisible,
  ] = useState<boolean>(false)
  const [isAIConversionModalVisible, setIsAIConversionModalVisible] =
    useState<boolean>(false)
  const [isSourceFillModalVisible, setIsSourceFillModalVisible] =
    useState<boolean>(false)
  const [sourceFillCsv, setSourceFillCsv] = useState<string>("")
  const [isTestSurveyOpen, setIsTestSurveyOpen] = useState<boolean>(false)

  const {
    downloadDatamapCSV,
    isDownloadingDatamapCSV,
    exportToHTML,
    getDatamapCSV,
  } = useEngine()

  // State to track if formatting (bold/italic) is allowed at current cursor position
  const [formattingAllowed, setFormattingAllowed] = useState<boolean>(true)

  // Update formatting allowed state when editor selection changes
  useEffect(() => {
    if (!editor) return

    const updateFormattingState = () => {
      setFormattingAllowed(canApplyFormatting(editor))
    }

    // Initial check
    updateFormattingState()

    // Listen for selection and content changes
    editor.on("selectionUpdate", updateFormattingState)
    editor.on("update", updateFormattingState)

    return () => {
      editor.off("selectionUpdate", updateFormattingState)
      editor.off("update", updateFormattingState)
    }
  }, [editor])

  /**
   * Opens "Fill export map from source document".
   *
   * Fetches the TEMPLATE datamap (VARIABLE column and category codes empty) and
   * hands it to the fill modal in standalone mode: no collected data is needed,
   * so this runs before fieldwork and ends in a saved export map profile.
   *
   * The datamap is generated server-side from the SAVED questionnaire — any
   * pending editor changes must be saved before this is called.
   */
  const openSourceFill = async (): Promise<void> => {
    const hideLoading = notify.toast.loading("Preparing export map template...")
    try {
      const response = await getDatamapCSV(
        project.id,
        false, // no section prefix: imported data paths are ungrouped by default, and the map must match them
        EXPORT_VARIABLE_TYPES.NUMBERING,
        undefined, // variable style: the datamap dialog's setting is per-export
        true, // question text: the fill matches on the original wording when present
        currentVariant || undefined,
        true // template
      )
      const raw =
        typeof response.data === "string"
          ? response.data
          : String(response.data ?? "")
      setSourceFillCsv(raw)
      setIsSourceFillModalVisible(true)
    } catch (error) {
      notify.toast.error("Export map template could not be created!")
      console.error("Error creating export map template:", error)
    } finally {
      hideLoading()
    }
  }

  /**
   * After inserting an AI conversion: offer to carry the codes the converter
   * read from the document straight into the export map.
   *
   * @param info - What the conversion captured
   */
  const handleConversionInserted = (info: {
    sourceCodesCaptured: number
  }): void => {
    if (info.sourceCodesCaptured <= 0) return

    Modal.confirm({
      title: "Fill the export map now?",
      content: `Source codes were captured from the document (${info.sourceCodesCaptured} questions). The questionnaire will be saved first, then the export map can be filled from those codes and saved as a profile.`,
      okText: "Save and fill",
      cancelText: "Later",
      onOk: async () => {
        // Order matters: the template datamap is generated from the SAVED
        // questionnaire, so the just-inserted content must be persisted first.
        if (onSave) await onSave()
        await openSourceFill()
      },
    })
  }

  /**
   * Handles the click event on menu items.
   *
   * @param e - The click event object
   */
  const onClick = async (e: MenuClickEvent): Promise<void> => {
    const actionKey = e.key as keyof ReturnType<typeof actions>
    const actionMap = actions(editor)
    const action = actionMap[actionKey]

    if (e.key === "template") {
      // Templates are added in Master (the menu row is disabled on a variant).
      if (!inMaster) return
      // Open at once; the save runs meanwhile and "Use template" waits for it.
      setIsTemplatesOpen(true)
      void saveForTemplates()
    } else if (e.key === "export_to_decipher_xml") {
      setExportDecipherModalVisible(true)
    } else if (e.key === "export_to_confirmit_xml") {
      setExportConfirmitModalVisible(true)
    } else if (e.key === "export_data_map") {
      setIsExportDatamapModalVisible(true)
    } else if (e.key === "fill_export_map_source") {
      await openSourceFill()
    } else if (e.key === "export_questionnaire") {
      setIsExportQuestionnaireModalVisible(true)
    } else if (e.key === "convert_questionnaire_ai") {
      setIsAIConversionModalVisible(true)
    } else if (e.key === "generate_dummy_data") {
      // Dummy data walks the SAVED questionnaire: save the editor first, then
      // open the Data Import tab on its Dummy data method.
      if (onSave) await onSave()
      navigate(
        `${ROUTES.PROJECT(project.id)}?tab=${PROJECT_TAB_KEYS.DATA_IMPORT}&method=dummy`
      )
    } else if (e.key === "test_survey") {
      // The test walks the SAVED questionnaire: save the editor first, then open the drawer.
      if (onSave) await onSave()
      setIsTestSurveyOpen(true)
    } else if (action && typeof action === "function") {
      action()
    }
  }

  const menuItems = useMemo(() => menuItemsFor(inMaster), [inMaster])

  // The start screen's tiles run the same actions as the menu.
  if (menuActionRef) menuActionRef.current = (key: string) => onClick({ key })

  /**
   * Opens the Templates dialog at `block`'s template wizard, filled in with
   * the block; from there it goes to the staging tray as usual, and
   * `onStaged` runs when it does. Templates are added in Master only, as
   * from the menu.
   */
  const openTemplateProposal = (block: StagedBlock, onStaged?: () => void): void => {
    if (!inMaster) return
    templateProposalStaged.current = onStaged
    setTemplatesInitialView({ ref: block.ref, block })
    setIsTemplatesOpen(true)
    void saveForTemplates()
  }
  if (templateProposalRef) templateProposalRef.current = openTemplateProposal

  /**
   * TODO Refactor this function to be in a helper file with same function in ExportToDecipherOptionsForm.component.jsx
   * Extracts unique languages from an array of variants and a project object.
   *
   * @param variants - An array of variant objects
   * @param project - A project object containing languages
   * @returns An array of unique languages
   */
  const getUniqueLanguages = (
    variants: Variant[],
    project: Project
  ): string[] => {
    const variantLanguages: string[] = variants.flatMap(
      (variant: Variant) => variant.languages
    )
    const allLanguages: string[] = [project.Designlanguage, ...variantLanguages]
    return [...new Set(allLanguages)]
  }

  // Default to MASTER if variants array is empty, or if only one language, default it
  const variantOptions: SelectOption[] =
    variants.length > 0
      ? variants.map((variant: Variant) => ({
          label: variant.name,
          value: variant.name,
        }))
      : [{ label: "MASTER", value: "MASTER" }]

  const languageOptions: SelectOption[] = getUniqueLanguages(
    variants,
    project
  ).map((language: string) => ({
    label: language,
    value: language,
  }))

  /**
   * Confirms the export of a data map template.
   *
   * @param filename - The name of the file for the export
   * @param additionalValues - Additional export options
   * @returns Promise that resolves when export is complete
   */
  const confirmDatamapExport = async (
    filename: string,
    additionalValues: Record<string, unknown>
  ): Promise<void> => {
    try {
      const values = additionalValues as unknown as DatamapExportValues
      await downloadDatamapCSV(
        project.id,
        filename,
        values.template,
        values.groupSections,
        values.variableNames,
        values.variableStyle,
        values.includeQuestionText
      )
      setIsExportDatamapModalVisible(false)
      notify.toast.success("Datamap created successfully!")
    } catch (error) {
      notify.toast.error("Datamap failed to be created!")
      console.error("Error creating datamap:", error)
    }
  }

  /**
   * Confirms the export of Questionnaire.
   *
   * @param filename - The name of the file for the export
   * @param additionalValues - Additional options for the export (language, variant and type)
   * @returns Promise that resolves when export is complete
   */
  const confirmQuestionnaireExport = async (
    filename: string,
    additionalValues: Record<string, unknown>
  ): Promise<void> => {
    const values = additionalValues as unknown as QuestionnaireExportValues
    const { type, variableNames, ...otherOptions } = values

    // Show loading message
    const hideLoading = notify.toast.loading(`Exporting questionnaire...`)

    try {
      // Create ConversionOptions object with required filename
      const options: ConversionOptions = {
        filename,
        ...otherOptions,
      }

      await exportToHTML(project.id, options, filename, type, variableNames)
      hideLoading()
      setIsExportQuestionnaireModalVisible(false)
      notify.toast.success("Questionnaire Exported successfully!")
    } catch (error) {
      hideLoading()
      notify.toast.error("Questionnaire failed to be created!")
      console.error("Error creating Questionnaire:", error)
    }
  }

  return (
    <>
      <ExportModal
        title="Export Data Map"
        confirm={confirmDatamapExport}
        cancel={setIsExportDatamapModalVisible}
        isVisible={isExportDatamapModalVisible}
        message="Please enter a filename for your data map export"
        additionalInputs={[
          {
            name: "template",
            type: UI_INPUT_TYPES.CHECKBOX,
            checkboxLabel: "Only Export Template",
            required: false,
          },
          {
            name: "groupSections",
            type: UI_INPUT_TYPES.CHECKBOX,
            checkboxLabel: "Group Questionnaire Sections",
            required: false,
          },
          {
            name: "variableNames",
            type: UI_INPUT_TYPES.SELECT,
            label: "Use as variable names",
            multiple: false,
            defaultValue: "numbering",
            options: [
              { value: "numbering", label: "Numbered automatically" },
              { value: "labels", label: "Generate from questionnaire labels" },
            ],
            required: false,
            disabled: (values: Record<string, unknown>) =>
              values.template === true,
          },
          {
            name: "variableStyle",
            type: UI_INPUT_TYPES.SELECT,
            label: "Variable style",
            multiple: false,
            defaultValue: VARIABLE_STYLE.DEFAULT,
            options: [
              { value: VARIABLE_STYLE.DEFAULT, label: "Default (s/p prefixes)" },
              { value: VARIABLE_STYLE.DECIPHER, label: "Decipher standard (r/c prefixes)" },
              { value: VARIABLE_STYLE.CONFIRMIT, label: "Confirmit (underscore suffixes)" },
            ],
            required: false,
          },
          {
            name: "includeQuestionText",
            type: UI_INPUT_TYPES.CHECKBOX,
            checkboxLabel: "Include full question text",
            required: false,
          },
        ]}
      />

      <ExportModal
        title="Export Questionnaire"
        confirm={confirmQuestionnaireExport}
        cancel={setIsExportQuestionnaireModalVisible}
        isVisible={isExportQuestionnaireModalVisible}
        message="Please enter a filename for your questionnaire export"
        additionalInputs={[
          {
            name: "languages",
            type: UI_INPUT_TYPES.SELECT,
            label: "Languages",
            multiple: true,
            defaultValue:
              languageOptions.length === 1
                ? [languageOptions[0].value]
                : undefined,
            options: languageOptions,
            required: true,
          },
          {
            name: "variant",
            type: UI_INPUT_TYPES.SELECT,
            label: "Variant",
            multiple: false,
            defaultValue:
              variantOptions.length === 1 ? variantOptions[0].value : undefined,
            options: variantOptions,
            required: true,
          },
          {
            name: "type",
            type: UI_INPUT_TYPES.SELECT,
            label: "Export Type",
            multiple: false,
            defaultValue: "word",
            options: [
              { value: "html", label: "HTML" },
              { value: "word", label: "Word" },
            ],
            required: true,
          },
          {
            name: "variableNames",
            type: UI_INPUT_TYPES.SELECT,
            label: "Use as variable names",
            multiple: false,
            defaultValue: "numbering",
            options: [
              { value: "numbering", label: "Numbered automatically" },
              { value: "labels", label: "Generate from questionnaire labels" },
            ],
            required: false,
            disabled: (values: Record<string, unknown>) =>
              values.template === true,
          },
          {
            name: "style",
            type: UI_INPUT_TYPES.SELECT,
            label: "Style",
            multiple: false,
            defaultValue: "default",
            options: [{ value: "default", label: "Default DATA(Q)" }],
          },
          {
            name: "notes",
            type: UI_INPUT_TYPES.SELECT,
            label: "Include Notes",
            multiple: true,
            defaultValue: [...new Set(getQuestionnaireNotes())],
            options: [...new Set(getQuestionnaireNotes())].map((note) => ({
              value: note,
              label: note,
            })),
          },
        ]}
      />

      <ProgressModal
        title="Creating Datamap"
        isLoading={isDownloadingDatamapCSV}
        duration={100}
      />

      <div className="flex flex-col w-full border-b border-gray-200 bg-white">
        <ExportToDecipherModal
          getQuestionnaireNotes={getQuestionnaireNotes}
          isExportDecipherModalVisible={isExportDecipherModalVisible}
          setExportDecipherModalVisible={setExportDecipherModalVisible}
          project={project}
          variants={variants}
        />

        <ExportToConfirmitModal
          isExportConfirmitModalVisible={isExportConfirmitModalVisible}
          setExportConfirmitModalVisible={setExportConfirmitModalVisible}
          project={project}
          variants={variants}
        />

        <TemplatesDialog
          open={isTemplatesOpen}
          onClose={() => {
            setIsTemplatesOpen(false)
            setTemplatesInitialView(undefined)
            templateProposalStaged.current = undefined
          }}
          initialView={templatesInitialView}
          onProposalStaged={() => templateProposalStaged.current?.()}
          saveState={templatesSave}
          onRetrySave={() => void saveForTemplates()}
          editor={editor}
          projectId={project.id}
          owner={project.Created_by}
          projectName={project.Name}
        />

        <AIConversionModal
          isVisible={isAIConversionModalVisible}
          onClose={() => setIsAIConversionModalVisible(false)}
          projectId={project.id}
          editor={editor}
          onInserted={handleConversionInserted}
        />

        <ExportMapSourceFillModal
          open={isSourceFillModalVisible}
          onClose={() => setIsSourceFillModalVisible(false)}
          projectId={project.id}
          mode="standalone"
          initialCsv={sourceFillCsv}
        />

        <SurveyTestDrawer
          open={isTestSurveyOpen}
          onClose={() => setIsTestSurveyOpen(false)}
          projectId={project.id}
          owner={project.Created_by}
          variants={variants}
          masterLanguages={masterLanguages}
          currentVariant={currentVariant ?? ""}
          designLanguage={project.Designlanguage}
          editor={editor}
          onSave={onSave}
        />

        {/* Row 1: the questionnaire as a whole (menus, variant, translations, metadata, preview) */}
        <div
          data-testid="toolbar-row-1"
          className="toolbar-row-1 flex items-center gap-1 px-2 h-10 border-b border-gray-100 overflow-hidden"
        >
          <Menu
            className="questionnaire-menu flex-none border-none !leading-[38px]"
            onClick={onClick}
            selectedKeys={[]}
            mode="horizontal"
            items={menuItems}
            disabled={isConnecting}
            disabledOverflow
          />
          <div className="toolbar-divider" />
          {toolbarExtras}
          {/* Outside the editor page (no assistant provider) there is no toggle */}
          {assistantScreen && (
            <AssistantToolbarToggle projectId={assistantScreen.projectId} />
          )}
        </div>

        {/* Row 2: editing tools */}
        <div
          data-testid="toolbar-row-2"
          className={`flex items-center gap-0.5 px-2 h-9 bg-gray-50 ${
            isConnecting
              ? "pointer-events-none opacity-50"
              : "pointer-events-auto"
          }`}
        >
          <ToolbarIconButton
            title="Undo (Ctrl+Z)"
            icon={faUndo}
            onClick={() => {
              editor?.chain().focus().undo().run()
              refreshHighlight?.()
            }}
            disabled={isConnecting || !editor?.can().undo()}
          />
          <ToolbarIconButton
            title="Redo (Ctrl+Y / Ctrl+Shift+Z)"
            icon={faRedo}
            onClick={() => {
              editor?.chain().focus().redo().run()
              refreshHighlight?.()
            }}
            disabled={isConnecting || !editor?.can().redo()}
          />

          <div className="toolbar-divider" />

          <ToolbarIconButton
            title={
              formattingAllowed
                ? "Bold (Ctrl+B)"
                : "Formatting not allowed in labels"
            }
            icon={faBold}
            active={!!editor?.isActive("bold")}
            onClick={() =>
              formattingAllowed && editor?.chain().focus().toggleBold().run()
            }
            disabled={isConnecting || !formattingAllowed}
          />
          <ToolbarIconButton
            title={
              formattingAllowed
                ? "Italic (Ctrl+I)"
                : "Formatting not allowed in labels"
            }
            icon={faItalic}
            active={!!editor?.isActive("italic")}
            onClick={() =>
              formattingAllowed && editor?.chain().focus().toggleItalic().run()
            }
            disabled={isConnecting || !formattingAllowed}
          />

          <div className="toolbar-divider" />

          <ToolbarIconButton
            title="Toggle Highlighting"
            active={highlightingEnabled}
            onClick={() => toggleHighlighting()}
            disabled={isConnecting}
            className="!w-auto !px-1.5"
          >
            (Q)
          </ToolbarIconButton>
          <ToolbarIconButton
            ref={elementHighlightButtonRef}
            title="Toggle Element Highlighting"
            icon={faHighlighter}
            active={elementHighlightingEnabled}
            onClick={() => toggleElementHighlighting()}
            disabled={isConnecting}
          />
          <ToolbarIconButton
            title="Toggle Line Numbers"
            icon={faListOl}
            active={lineNumbersEnabled}
            onClick={() => toggleLineNumbers()}
            disabled={isConnecting}
          />
          <ToolbarIconButton
            ref={structurePanelButtonRef}
            title="Structure Panel (Ctrl+Shift+E)"
            icon={faSitemap}
            active={!!structurePanelVisible}
            onClick={() => toggleStructurePanel?.()}
            disabled={isConnecting || qnrEmpty}
          />

          <div className="toolbar-divider" />

          <ToolbarIconButton
            ref={addElementButtonRef}
            title="Add Survey Element"
            icon={faPlus}
            active={!!addElementModalVisible}
            onClick={() => toggleAddElementModal?.()}
            disabled={isConnecting || qnrEmpty}
          />
          <ToolbarIconButton
            title="Check Questionnaire"
            icon={faArrowRightToBracket}
            onClick={() => checkQuestionnaire(project.id)}
            disabled={isConnecting}
          />
          <ToolbarIconButton
            title="Format guide documentation"
            icon={faBook}
            onClick={() => window.open(DOCS_LINKS.FORMAT_GUIDE, "_blank")}
            disabled={isConnecting}
          />

          <div className="flex-1" />

          <div className="relative">
            <ToolbarIconButton
              title="Comments Panel"
              icon={faComments}
              active={!!commentsPanelVisible}
              onClick={() => toggleCommentsPanel?.()}
              disabled={isConnecting}
            />
            {/* New comments indicator */}
            {hasNewComments && !commentsPanelVisible && (
              <span className="absolute top-0.5 right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full border border-white pointer-events-none" />
            )}
          </div>
          <ToolbarIconButton
            title="Project history"
            icon={faClockRotateLeft}
            active={!!historyPanelVisible}
            onClick={() => toggleHistoryPanel?.()}
            disabled={isConnecting}
          />
        </div>
      </div>
    </>
  )
}

export default QuestionnaireMenuBar
